import React from 'react';
import { ArrowRight, Github, Linkedin, Mail, Award } from 'lucide-react';
import { Button } from './ui/button';
import { getCurrentAge } from '../utils/age';

const Hero = () => {
  const currentAge = getCurrentAge();

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-16 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 left-1/4 w-72 h-72 bg-[#BCA4E3]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#A88DD6]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-5xl mx-auto w-full py-20 relative z-10">
        <div className="flex flex-col items-center text-center space-y-8">
          {/* Main Heading */}
          <div className="space-y-4 animate-fade-in-up">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight bg-gradient-to-r from-white via-[#D0C0EB] to-[#BCA4E3] bg-clip-text text-transparent">
              Mikolaj Wojtowicz
            </h1>
            <p className="text-xl sm:text-2xl text-[#BCA4E3] font-medium animate-fade-in-up delay-100">
              Aspiring DevOps Engineer & Student
            </p>
          </div>

          {/* Description */}
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed animate-fade-in-up delay-200">
            I'm a {currentAge}-year-old student passionate about cloud infrastructure, automation, and continuous integration.
            Certified in Cisco CCNAv7.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in-up delay-300">
            <Button
              size="lg"
              className="group bg-[#BCA4E3] hover:bg-[#A88DD6] text-slate-900 shadow-lg shadow-[#BCA4E3]/20 hover:shadow-[#BCA4E3]/30 transition-all font-semibold relative overflow-hidden"
              onClick={() => document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="relative z-10">View My Work</span>
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              <span className="absolute inset-0 bg-gradient-to-r from-[#A88DD6] to-[#BCA4E3] opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-600 transition-all relative overflow-hidden"
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="relative z-10">Get In Touch</span>
              <span className="absolute inset-0 bg-slate-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </Button>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-6 pt-8 animate-fade-in-up delay-300">
            <a
              href="https://to.wojtowicz.dev/github"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#BCA4E3] transition-all transform hover:scale-125 duration-200 p-2 rounded-full hover:bg-slate-800/50"
              aria-label="GitHub"
            >
              <Github className="w-6 h-6" />
            </a>
            <a
              href="https://to.wojtowicz.dev/linkedin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#BCA4E3] transition-all transform hover:scale-125 duration-200 p-2 rounded-full hover:bg-slate-800/50"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-6 h-6" />
            </a>
            <a
              href="mailto:mikolaj@wojtowicz.dev"
              className="text-slate-400 hover:text-[#BCA4E3] transition-all transform hover:scale-125 duration-200 p-2 rounded-full hover:bg-slate-800/50"
              aria-label="Email Mikolaj"
            >
              <Mail className="w-6 h-6" />
            </a>
            <a
              href="https://to.wojtowicz.dev/credly"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#BCA4E3] transition-all transform hover:scale-125 duration-200 p-2 rounded-full hover:bg-slate-800/50"
              aria-label="Credly Certifications"
            >
              <Award className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
